import { connect } from 'react-redux';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useState, useEffect } from 'react';

import { Box, TableBody, TableHead, TableRow } from '@mui/material';

// Actions
import { loading, loadingSuccess } from '@actions/loading';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

// Common
import { GenericCancelButton, GenericSaveButton } from '@commons/Buttons';
import { handleKeyDown } from '@commons/utils/dataGrid';
import { ProgressCircle } from '@commons/ProgressCircle';
import { TIME_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

// Services
import storeService from '@services/store';

// Design
import {
  AllocationPercentageCell,
  StyledTable,
  StyledTableContainer,
  StyledTableHeaderCell,
  StyledTableRow,
  StyledTextField,
  TimeSlotInfoCell,
} from './styledMUIComponents';
import {
  ButtonsContainer,
  CloseButton,
  Container,
  ContentContainer,
  FooterContainer,
  HeaderContainer,
  Title,
  TotalRatioContainer,
} from './styledComponents';

const ProductionTimeSlotAllocationManagement = (props) => {
  const {
    // From redux
    showErrorMessage,
    showSuccessMessage,
    pageLoading,
    pageLoaded,
    // From parent
    storeName,
    storeId,
    storeTimeSlots,
    selectedProductIds,
    onClose,
  } = props;

  /**********
   * STATES *
   **********/
  const [isLoading, setIsLoading] = useState(false);

  const [allocations, setAllocations] = useState([]);

  /*************
   * EFFECTS *
   *************/

  useEffect(() => {
    setAllocations(
      storeTimeSlots.map((slot) => ({
        ...slot,
        startTime: moment(slot.startTime, TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE).format('HH:mm'),
        endTime: moment(slot.endTime, TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE).format('HH:mm'),
        ratio: null,
      })),
    );
  }, [storeTimeSlots]);

  /*************
   * HANDLERS *
   *************/

  const saveProductTimeSlotAllocations = async () => {
    setIsLoading(true);
    pageLoading();

    try {
      const formattedAllocations = allocations.map((slot) => ({
        productionTimeSlotId: slot.id,
        ratio: slot.ratio ?? 0,
      }));

      await storeService.updateProductTimeSlotsAllocations(
        storeId,
        selectedProductIds,
        formattedAllocations,
      );

      showSuccessMessage(i18next.t('ADMIN.STORES.TIME_SLOTS_ALLOCATIONS_MANAGEMENT_SUCCESS'));
      onClose({ needRefresh: true });
    } catch (error) {
      showErrorMessage(i18next.t('ADMIN.STORES.TIME_SLOTS_ALLOCATIONS_MANAGEMENT_ERROR'));
    } finally {
      setIsLoading(false);
      pageLoaded();
    }
  };

  const resetStateAndClose = () => {
    onClose();
  };

  const updateAllocationPercentage = (id, value) => {
    // Convert to number and validate range
    const numericValue = parseFloat(value);

    // Enforce value of 0 if attempting to clear the field
    if (value === '' || value === null || value === undefined) {
      setAllocations(allocations.map((slot) => (slot.id === id ? { ...slot, ratio: 0 } : slot)));
      return;
    }

    // Only update if the value is a valid number between 0 and 100 (inclusive)
    if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 100) {
      setAllocations(
        allocations.map((slot) => (slot.id === id ? { ...slot, ratio: numericValue } : slot)),
      );
    }
  };

  const totalRatio = parseFloat(
    allocations.reduce(
      (acc, slot) => (slot.ratio !== null ? acc + parseFloat(slot.ratio) : acc),
      0,
    ),
  );

  return (
    <Container>
      <HeaderContainer>
        <CloseButton
          alt="close"
          src="/images/inpulse/close-black-small.svg"
          onClick={resetStateAndClose}
        />
        <Title>{i18next.t('ADMIN.STORES.TIME_SLOTS_ALLOCATIONS_MANAGEMENT_TITLE')}</Title>
        <Text color={ENUM_COLORS.IP_BLACK_4}>{storeName}</Text>
      </HeaderContainer>

      <ContentContainer>
        <Box sx={{ width: '100%' }}>
          <StyledTableContainer>
            <StyledTable aria-label="time slots table">
              <TableHead>
                <TableRow>
                  <StyledTableHeaderCell>{i18next.t('GENERAL.TIME_SLOT')}</StyledTableHeaderCell>
                  <StyledTableHeaderCell>
                    {i18next.t('ADMIN.STORES.TIME_SLOTS_ALLOCATION_PERCENTAGE')}
                  </StyledTableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allocations.map((slot) => (
                  <StyledTableRow key={slot.id}>
                    <TimeSlotInfoCell>
                      <Text>{slot.name}</Text>
                      <Text color={ENUM_COLORS.IP_BLACK_5} font={ENUM_FONTS.TEXT_SMALL}>
                        {slot.startTime}-{slot.endTime}
                      </Text>
                    </TimeSlotInfoCell>
                    <AllocationPercentageCell>
                      <StyledTextField
                        placeholder={'-'}
                        slotProps={{
                          input: {
                            min: 0,
                            max: 100,
                          },
                        }}
                        type="number"
                        value={slot.ratio ?? 0}
                        onChange={(e) => updateAllocationPercentage(slot.id, e.target.value)}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={handleKeyDown}
                      />
                    </AllocationPercentageCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </StyledTable>
          </StyledTableContainer>
        </Box>
      </ContentContainer>

      <FooterContainer>
        <TotalRatioContainer>
          <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_MIDDLE_BOLD}>
            {i18next.t('ADMIN.STORES.TIME_SLOTS_TOTAL_RATIO')}:
          </Text>
          <ProgressCircle
            customSize={40}
            font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}
            percentage={totalRatio}
            useIsComplete={false}
          />
        </TotalRatioContainer>
        <ButtonsContainer>
          <GenericCancelButton handleClick={resetStateAndClose} minWidth={160} />
          <GenericSaveButton
            handleClick={saveProductTimeSlotAllocations}
            isDisabled={isLoading || totalRatio !== 100}
            minWidth={160}
          />
        </ButtonsContainer>
      </FooterContainer>
    </Container>
  );
};

const mapDispatchToProps = (dispatch) => ({
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
});

export default connect(null, mapDispatchToProps)(ProductionTimeSlotAllocationManagement);
